'use client';

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Zap, ArrowRight, User, Mail, Lock, CheckCircle2, AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { formatAuthError, type FormattedError } from '../lib/errorHandler';

export function Signup() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorObj, setErrorObj] = useState<FormattedError | null>(null);

    const { signup } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const plan = searchParams.get('plan');

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorObj(null);

        // Basic client-side validation
        if (password.length < 6) {
            setErrorObj({
                title: 'Password Too Short',
                message: 'Your password must be at least 6 characters long.',
                suggestion: 'Use a mix of letters and numbers for better security.'
            });
            return;
        }

        setIsLoading(true);
        try {
            await signup({
                username: email.trim(),
                password,
                options: {
                    userAttributes: {
                        email: email.trim(),
                        name: name.trim(),
                    }
                }
            });
            navigate('/');
        } catch (error) {
            console.error('Signup error:', error);
            const formatted = formatAuthError(error);
            setErrorObj(formatted);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-card flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                {/* Logo */}
                <Link to="/" className="flex justify-center items-center gap-2 mb-8 group">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary shadow-md group-hover:scale-105 transition-transform">
                        <Zap className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <span className="text-2xl font-extrabold text-foreground tracking-tight">BgRemover</span>
                </Link>

                {/* Heading */}
                <h2 className="text-3xl font-bold text-foreground text-center mb-2">
                    Get started
                </h2>
                <p className="text-center text-muted-foreground mb-8 text-sm">
                    Create your account to start removing backgrounds
                </p>
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-card border border-border rounded-2xl shadow-xl p-8 backdrop-blur-sm">
                    {/* Plan Badge */}
                    {plan && (
                        <div className="mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary font-medium">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {plan === 'api' ? 'API Plan Selected' : 'Pro Plan Selected'}
                        </div>
                    )}

                    {/* Production Error Banner */}
                    {errorObj && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-sm">
                                <h4 className="font-semibold text-red-600 dark:text-red-400">
                                    {errorObj.title}
                                </h4>
                                <p className="text-muted-foreground mt-0.5 leading-relaxed">
                                    {errorObj.message}
                                </p>
                                {errorObj.suggestion && (
                                    <p className="text-xs text-muted-foreground mt-1.5 font-medium">
                                        💡 {errorObj.suggestion}
                                    </p>
                                )}
                                {errorObj.actionLink && errorObj.actionLabel && (
                                    <Link
                                        to={errorObj.actionLink}
                                        className="inline-block mt-2 text-xs font-semibold text-primary hover:underline"
                                    >
                                        {errorObj.actionLabel} &rarr;
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}

                    <form className="space-y-4" onSubmit={handleSignup}>
                        {/* Name Field */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">
                                Full Name
                            </label>
                            <div className="relative">
                                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        if (errorObj) setErrorObj(null);
                                    }}
                                    placeholder="John Doe"
                                    className="block w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                                />
                            </div>
                        </div>

                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (errorObj) setErrorObj(null);
                                    }}
                                    placeholder="you@example.com"
                                    className="block w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    required
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (errorObj) setErrorObj(null);
                                    }}
                                    placeholder="At least 6 characters"
                                    className="block w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-70 transition-all text-sm shadow-md mt-6"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                <>
                                    Create Account
                                    <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Sign In Link */}
                    <div className="mt-6 pt-6 border-t border-border text-center">
                        <p className="text-sm text-muted-foreground">
                            Already have an account?{' '}
                            <Link to="/login" className="font-semibold text-primary hover:text-primary/80 transition-colors">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
